import { ErrorSurface, type ErrorSurfaceAction } from '@/components/ErrorSurface'
import { classifyRouteError } from '@/error/routeErrorKind'
import { getAuth } from '@/auth/authRef'
import { getRouter } from '@/router/router'
import { getString } from '@/notFound/i18n/bundle.generated'

// Bullfrog is served from the US retail domain, so the per-market plan-settings
// URL green hornet derives via getRetailDomain() collapses to this fixed host.
const SHOW_PLANS_URL = 'https://www.amazon.com/music/settings?ref=dp_amp_settings_yasettings_click'

/**
 * Recovery surface for a failed route transition / render or a page-loader
 * failure that would otherwise leave a blank stage. Mirrors green hornet's
 * per-ErrorSource split: a signed-out unauthorized error offers Sign-in +
 * Show-plans, a network error offers Try again, a server error offers Try
 * again + home; anything unclassified keeps the generic "something went wrong".
 */
export function ErrorPage(error?: unknown) {
    switch (classifyRouteError(error)) {
        case 'unauthorized':
            return unauthorizedSurface()
        case 'network':
            return networkSurface()
        case 'server':
            return serverSurface()
        default:
            return defaultSurface()
    }
}

function unauthorizedSurface() {
    const signedIn = getAuth().isSignedIn()
    const actions: ErrorSurfaceAction[] = signedIn
        ? [homeAction()]
        : [
              {
                  label: getString('dmusic_unauthorized_login_link'),
                  onClick: () => getAuth().signIn(),
              },
              {
                  label: getString('dmusic_unauthorized_upgrade_link'),
                  onClick: () => window.open(SHOW_PLANS_URL, '_blank', 'noopener,noreferrer'),
              },
          ]
    return ErrorSurface({
        message: getString('dmusic_unauthorized_primary_text'),
        detail: getString(
            signedIn
                ? 'dmusic_unauthorized_logged_in_secondary_text'
                : 'dmusic_unauthorized_secondary_text'
        ),
        actions,
    })
}

function networkSurface() {
    return ErrorSurface({
        message: getString('dmusic_network_error_primary_text'),
        detail: getString('dmusic_network_error_secondary_text'),
        actions: [retryAction()],
    })
}

function serverSurface() {
    return ErrorSurface({
        message: getString('dmusic_default_error_primary_text'),
        detail: getString('dmusic_server_error_suggestion_text'),
        actions: [retryAction(), homeAction()],
    })
}

function defaultSurface() {
    return ErrorSurface({
        message: getString('dmusic_default_error_primary_text'),
        detail: getString('dmusic_default_error_secondary_text'),
        actions: [retryAction(), homeAction()],
    })
}

function retryAction(): ErrorSurfaceAction {
    return {
        label: getString('dmusic_network_error_try_again_link'),
        onClick: () => getRouter().hardReload(),
    }
}

function homeAction(): ErrorSurfaceAction {
    return {
        href: '/',
        label: getString('dmusic_default_error_homepage_link'),
        navigateOptions: { scroll: 'restore' },
    }
}
